import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { EmailMessageView, FeatureFlagView, PermissionKey } from '@agency/contracts';
import { Badge, Button, EmptyState, Notice, Skeleton, Tabs, tabId, tabPanelId, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, get, patch } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDate, relative } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { MarketsTab, ServicesTab } from './catalog-tabs.tsx';
import { GlossaryTab } from './glossary-tab.tsx';
import { LanguagesTab } from './languages-tab.tsx';
import { BrandForm, DefaultSeoForm, FooterForm, NavigationForm } from './site-forms.tsx';

type SystemStatus = { appEnv: string; email: { driver: string; delivers: boolean }; capabilities: { key: string; enabled: boolean; reason?: string }[] };
const errText = (e: unknown) => (e instanceof ApiClientError ? `${e.message}${e.requestId ? ` (Ref ${e.requestId})` : ''}` : 'Something went wrong. Try again.');

type Tab = 'general' | 'brand' | 'navigation' | 'footer' | 'seo' | 'languages' | 'markets' | 'services' | 'glossary';
// Each tab needs the permission its endpoint requires; the server still enforces it.
const TABS: { id: Tab; label: string; perm: PermissionKey }[] = [
  { id: 'general', label: 'General', perm: 'settings.read' },
  { id: 'brand', label: 'Brand', perm: 'settings.read' },
  { id: 'navigation', label: 'Navigation', perm: 'settings.read' },
  { id: 'footer', label: 'Footer', perm: 'settings.read' },
  { id: 'seo', label: 'Default SEO', perm: 'settings.read' },
  { id: 'languages', label: 'Languages', perm: 'settings.read' },
  { id: 'markets', label: 'Markets', perm: 'landings.read' },
  { id: 'services', label: 'Services', perm: 'landings.read' },
  { id: 'glossary', label: 'Glossary', perm: 'content.read' },
];

export function SettingsPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabs = useMemo(() => TABS.filter((t) => can(t.perm)), [can]);
  const tab = tabs.find((t) => t.id === params.get('tab'))?.id ?? 'general';
  const setTab = (t: Tab) => setParams(t === 'general' ? {} : { tab: t }, { replace: true });
  return (
    <>
      <div className="pagehead">
        <div><h1>Settings</h1><p>What this deployment can do, how the public site is branded and navigated, and the languages, markets and services it works with.</p></div>
      </div>
      <Tabs<Tab> id="settings-tabs" label="Settings sections" tabs={tabs} value={tab} onChange={setTab} />
      <div role="tabpanel" id={tabPanelId('settings-tabs')} aria-labelledby={tabId('settings-tabs', tab)} style={{ paddingTop: 16 }}>
        {tab === 'general' ? <div className="stack"><StatusSection /><FlagsSection /><DevMailSection /></div> : null}
        {tab === 'brand' ? <div className="stack"><BrandForm /></div> : null}
        {tab === 'navigation' ? <div className="stack"><NavigationForm /></div> : null}
        {tab === 'footer' ? <div className="stack"><FooterForm /></div> : null}
        {tab === 'seo' ? <div className="stack"><DefaultSeoForm /></div> : null}
        {tab === 'languages' ? <div className="stack stack--wide"><LanguagesTab /></div> : null}
        {tab === 'markets' ? <div className="stack stack--wide"><MarketsTab /></div> : null}
        {tab === 'services' ? <div className="stack stack--wide"><ServicesTab /></div> : null}
        {tab === 'glossary' ? <div className="stack stack--wide"><GlossaryTab /></div> : null}
      </div>
    </>
  );
}

function StatusSection() {
  const q = useQuery<SystemStatus, ApiClientError>({ queryKey: ['system-status'], queryFn: () => get('/system/status') });
  return (
    <section className="panel" aria-labelledby="status-h">
      <h2 id="status-h">Environment and integrations</h2>
      {q.isLoading ? <Skeleton width="100%" height={100} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : (
        <>
          <p>Environment: <Badge tone={q.data!.appEnv === 'production' ? 'warning' : 'info'}>{q.data!.appEnv}</Badge></p>
          <div className="ag-table-wrap" {...scrollRegion('Integration status')}>
            <table className="ag-table">
              <caption className="ag-visually-hidden">Integration status</caption>
              <thead><tr><th scope="col">Capability</th><th scope="col">Status</th><th scope="col">Detail</th></tr></thead>
              <tbody>
                {q.data!.capabilities.map((c) => (
                  <tr key={c.key}>
                    <td className="ag-mono">{c.key}</td>
                    <td><Badge tone={c.enabled ? 'success' : 'neutral'}>{c.enabled ? 'Configured' : 'Not configured'}</Badge></td>
                    <td>{c.reason ?? 'Credentials present.'}</td>
                  </tr>
                ))}
                <tr>
                  <td className="ag-mono">email</td>
                  <td><Badge tone={q.data!.email.delivers ? 'success' : 'warning'}>{q.data!.email.delivers ? 'Delivering' : 'Not delivering'}</Badge></td>
                  <td>{q.data!.email.delivers ? `Driver: ${q.data!.email.driver}` : 'Development capture only. Invitations and password resets are stored below, not sent.'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function FlagsSection() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery<FeatureFlagView[], ApiClientError>({ queryKey: ['flags'], queryFn: () => get('/flags') });
  const m = useMutation({
    mutationFn: (v: { key: string; enabled?: boolean; rolloutPercent?: number }) => patch(`/flags/${v.key}`, { enabled: v.enabled, rolloutPercent: v.rolloutPercent }),
    // Optimistic: the switch moves immediately and snaps back with an explanation if the server refuses.
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ['flags'] });
      const prev = qc.getQueryData<FeatureFlagView[]>(['flags']);
      qc.setQueryData<FeatureFlagView[]>(['flags'], (old) => old?.map((f) => (f.key === v.key ? { ...f, enabled: v.enabled ?? f.enabled, rolloutPercent: v.rolloutPercent ?? f.rolloutPercent } : f)));
      return { prev };
    },
    onSuccess: (_r, v) => toast({ tone: 'success', message: `Updated ${v.key}.` }),
    onError: (e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(['flags'], ctx.prev); toast({ tone: 'error', message: errText(e) }); },
    onSettled: () => qc.invalidateQueries({ queryKey: ['flags'] }),
  });
  const manage = can('settings.manage');
  return (
    <section className="panel" aria-labelledby="flags-h">
      <h2 id="flags-h">Feature flags</h2>
      <p className="ag-muted">Everything starts off. A staged rollout shows the feature to a stable share of people, so nobody flips back and forth as the percentage grows.</p>
      {q.isLoading ? <Skeleton width="100%" height={120} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : (
        <div className="ag-table-wrap" {...scrollRegion('Feature flags')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Feature flags</caption>
            <thead><tr><th scope="col">Flag</th><th scope="col">Enabled</th><th scope="col" className="ag-num">Rollout</th><th scope="col">Updated</th></tr></thead>
            <tbody>
              {q.data!.map((f) => (
                <tr key={f.key}>
                  <td><span className="ag-cell-2"><span className="ag-mono">{f.key}</span><span>{f.description}</span></span></td>
                  <td>
                    <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                      <input type="checkbox" checked={f.enabled} disabled={!manage} onChange={(e) => m.mutate({ key: f.key, enabled: e.target.checked })} />
                      <span>{f.enabled ? 'On' : 'Off'}<span className="ag-visually-hidden"> for {f.key}</span></span>
                    </label>
                  </td>
                  <td className="ag-num">
                    <label>
                      <span className="ag-visually-hidden">Rollout percent for {f.key}</span>
                      <select className="ag-input ag-select" disabled={!manage || !f.enabled} value={f.rolloutPercent} onChange={(e) => m.mutate({ key: f.key, rolloutPercent: Number(e.target.value) })}>
                        {[1, 5, 10, 25, 50, 75, 100].map((p) => <option key={p} value={p}>{p}%</option>)}
                        {![1, 5, 10, 25, 50, 75, 100].includes(f.rolloutPercent) ? <option value={f.rolloutPercent}>{f.rolloutPercent}%</option> : null}
                      </select>
                    </label>
                  </td>
                  <td>{relative(f.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!manage ? <p className="ag-muted">You can view flags but not change them.</p> : null}
    </section>
  );
}

function DevMailSection() {
  const { can } = useAuth();
  const q = useQuery<EmailMessageView[], ApiClientError>({ queryKey: ['dev-mail'], queryFn: () => get('/dev/mail'), enabled: can('settings.manage'), retry: false });
  if (!can('settings.manage')) return null;
  if (q.isError && q.error.status === 404) return null;
  return (
    <section className="panel" aria-labelledby="mail-h">
      <h2 id="mail-h">Captured email (development)</h2>
      <Notice tone="warning" title="Nothing here was sent" live={false}>These are the messages the app would have emailed. They contain one-time links, so this view only exists outside production.</Notice>
      {q.isLoading ? <Skeleton width="100%" height={80} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? <EmptyState title="No messages captured" icon="mail">Invite someone or request a password reset to see the message here.</EmptyState> : (
        <ul className="maillist">
          {q.data!.map((m) => (
            <li key={m.id}>
              <details>
                <summary><strong>{m.subject}</strong> <span className="ag-muted">to {m.toEmail} · {fmtDate(m.createdAt)}</span></summary>
                <pre className="mono-block">{m.bodyText}</pre>
              </details>
            </li>
          ))}
        </ul>
      )}
      <div><Button size="sm" variant="ghost" icon="refresh" onClick={() => void q.refetch()}>Refresh</Button></div>
    </section>
  );
}
